/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PermissionAction } from "../../flatbuffers/generated/archive/permission-action";
import type { RootState } from "../../app/store";

/**
 * Per-entity scope maps. Each key is a UUID string; the value is a Set of
 * PermissionAction values granted on that entity. An empty map means
 * "no restriction" (allow-all), not "deny all" — this is the empty⇒allow-all
 * invariant enforced by all consumers.
 *
 * Redux Toolkit's serializability check is disabled project-wide
 * (serializableCheck: false in store.ts), so Set is safe to store here.
 */
export interface ScopeState {
  bankScope: Record<string, Set<PermissionAction>>;
  liftScope: Record<string, Set<PermissionAction>>;
  floorScope: Record<string, Set<PermissionAction>>;
  /** true when the user's role carries at least one bank grant row */
  hasBankRows: boolean;
  /** true when the user's role carries at least one lift grant row */
  hasLiftRows: boolean;
  /** true when the user's role carries at least one floor grant row */
  hasFloorRows: boolean;
}

/**
 * One permission grant against a single entity — the bank, lift or floor it applies to,
 * and the actions it allows.
 */
export interface ScopeGrant {
  entityId: string;
  action: PermissionAction;
}

/**
 * Every scope grant attached to a role, split by entity kind.
 */
export interface RoleScopeGrants {
  bankGrants: ScopeGrant[];
  liftGrants: ScopeGrant[];
  floorGrants: ScopeGrant[];
}

const initialState: ScopeState = {
  bankScope: {},
  liftScope: {},
  floorScope: {},
  hasBankRows: false,
  hasLiftRows: false,
  hasFloorRows: false,
};

function buildScopeMap(grants: ScopeGrant[]): Record<string, Set<PermissionAction>> {
  const map: Record<string, Set<PermissionAction>> = {};
  grants.forEach((grant) => {
    if (!grant.entityId) return;
    if (!map[grant.entityId]) {
      map[grant.entityId] = new Set<PermissionAction>();
    }
    map[grant.entityId].add(grant.action);
  });
  return map;
}

const scopeSlice = createSlice({
  name: "scope",
  initialState,
  reducers: {
    setRoleScope(state, action: PayloadAction<RoleScopeGrants>) {
      const { bankGrants, liftGrants, floorGrants } = action.payload;
      state.bankScope = buildScopeMap(bankGrants) as ScopeState["bankScope"];
      state.liftScope = buildScopeMap(liftGrants) as ScopeState["liftScope"];
      state.floorScope = buildScopeMap(floorGrants) as ScopeState["floorScope"];
      state.hasBankRows = bankGrants.length > 0;
      state.hasLiftRows = liftGrants.length > 0;
      state.hasFloorRows = floorGrants.length > 0;
    },
    clearScope(state) {
      state.bankScope = {};
      state.liftScope = {};
      state.floorScope = {};
      state.hasBankRows = false;
      state.hasLiftRows = false;
      state.hasFloorRows = false;
    },
  },
});

export const { setRoleScope, clearScope } = scopeSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * The whole scope slice.
 */
export function selectScopeState(state: RootState): ScopeState {
  return state.scope;
}

/**
 * Permitted actions per bank id. An empty map means no bank-level restriction, which
 * is allow-all rather than deny-all.
 */
export function selectBankScope(state: RootState): Record<string, Set<PermissionAction>> {
  return state.scope.bankScope;
}

/**
 * Permitted actions per lift id.
 */
export function selectLiftScope(state: RootState): Record<string, Set<PermissionAction>> {
  return state.scope.liftScope;
}

/**
 * Permitted actions per floor id.
 */
export function selectFloorScope(state: RootState): Record<string, Set<PermissionAction>> {
  return state.scope.floorScope;
}

/**
 * Whether any bank-level grant rows exist at all.
 *
 * This is what distinguishes "no restriction configured" from "restricted to nothing";
 * without it an empty scope map is ambiguous.
 */
export function selectHasBankRows(state: RootState): boolean {
  return state.scope.hasBankRows;
}

/**
 * Whether any lift-level grant rows exist at all. See `selectHasBankRows`.
 */
export function selectHasLiftRows(state: RootState): boolean {
  return state.scope.hasLiftRows;
}

/**
 * Whether any floor-level grant rows exist at all. See `selectHasBankRows`.
 */
export function selectHasFloorRows(state: RootState): boolean {
  return state.scope.hasFloorRows;
}

export default scopeSlice.reducer;
